import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
} from 'libphonenumber-js';

// Converts an ISO 3166-1 alpha-2 code ("IN", "US"...) into its flag emoji
// by mapping each letter to its Unicode "regional indicator symbol".
// Note: emoji flags don't render on some older Windows configs (they show
// as text codes instead). If that matters for your user base, swap this
// for the `country-flag-icons` package (SVG flags) - same interface below,
// just replace `flag` with an <img src={Flags[code]} /> at render time.
const toFlagEmoji = (isoCode) =>
  isoCode
    .toUpperCase()
    .replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt(0)));

const countryNames = new Intl.DisplayNames(['en'], { type: 'region' });

// Full, production-scale country list - no manual maintenance required.
// libphonenumber-js ships this data and is kept in sync with ITU assignments.
export const COUNTRY_OPTIONS = getCountries()
  .map((code) => ({
    code, // ISO 3166-1 alpha-2, e.g. "IN"
    name: countryNames.of(code) || code,
    flag: toFlagEmoji(code),
    dialCode: `+${getCountryCallingCode(code)}`,
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

export const findCountryByCode = (isoCode) =>
  COUNTRY_OPTIONS.find((c) => c.code === isoCode);

export const findCountryByDialCode = (dialCode) =>
  COUNTRY_OPTIONS.find((c) => c.dialCode === dialCode);

export const DEFAULT_COUNTRY = findCountryByCode('IN');

const extractRegionFromLocale = (locale) => {
  if (!locale) {
    return null;
  }

  try {
    if (typeof Intl.Locale === 'function') {
      const parsedLocale = new Intl.Locale(locale);
      if (parsedLocale.region) {
        return parsedLocale.region.toUpperCase();
      }
    }
  } catch (error) {
    // Fallback parser below handles malformed locales.
  }

  const parts = String(locale)
    .replace('_', '-')
    .split('-')
    .map((part) => part.trim())
    .filter(Boolean);

  const regionPart = parts.find((part) => /^[A-Za-z]{2}$/.test(part));
  return regionPart ? regionPart.toUpperCase() : null;
};

export const detectUserCountry = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return DEFAULT_COUNTRY;
  }

  const localeCandidates = [
    ...(navigator.languages || []),
    navigator.language,
    Intl.DateTimeFormat().resolvedOptions().locale,
  ].filter(Boolean);

  for (let i = 0; i < localeCandidates.length; i += 1) {
    const region = extractRegionFromLocale(localeCandidates[i]);
    if (!region) {
      continue;
    }

    const matchedCountry = findCountryByCode(region);
    if (matchedCountry) {
      return matchedCountry;
    }
  }

  return DEFAULT_COUNTRY;
};

export const buildPhoneNumber = (country, localNumber) => {
  const digitsOnly = String(localNumber || '').replace(/\D/g, '');

  if (!digitsOnly) {
    return '';
  }

  return `${country.dialCode}${digitsOnly}`;
};

export const splitPhoneNumber = (phone, fallbackCountry = DEFAULT_COUNTRY) => {
  const rawPhone = String(phone || '').trim();

  if (!rawPhone) {
    return {
      country: fallbackCountry,
      localNumber: '',
    };
  }

  const parsed = parsePhoneNumberFromString(rawPhone);
  if (parsed?.country) {
    const parsedCountry = findCountryByCode(parsed.country);
    if (parsedCountry) {
      return {
        country: parsedCountry,
        localNumber: parsed.nationalNumber || '',
      };
    }
  }

  if (rawPhone.startsWith('+')) {
    const digits = rawPhone.replace(/\D/g, '');
    const sortedByDialCodeLength = [...COUNTRY_OPTIONS].sort(
      (a, b) => b.dialCode.length - a.dialCode.length
    );
    const matchedCountry = sortedByDialCodeLength.find((option) =>
      digits.startsWith(option.dialCode.replace('+', ''))
    );

    if (matchedCountry) {
      const dialDigits = matchedCountry.dialCode.replace('+', '');
      return {
        country: matchedCountry,
        localNumber: digits.slice(dialDigits.length),
      };
    }
  }

  return {
    country: fallbackCountry,
    localNumber: rawPhone.replace(/\D/g, ''),
  };
};
